import React from 'react';
import { WarningOutlined } from '@ant-design/icons';
import { Spin, Button, Space, Typography } from 'antd';
import PreparingIcon from '@icons/preparing.svg';
import { CardWrapper, Layer, Loader } from './styled';
import ProgressBar from './ProgressBar';
import { calcProgressDuration, secondsToMinutes } from './utils';
import { HEAVY_METRICS_MESSAGE } from '@utils/constants';
const { Text } = Typography;

const RETRY_TXT = {
  msg: 'Please try again',
  btn: 'Retry',
  err: 'Error loading data',
  heavyMetricMsg: HEAVY_METRICS_MESSAGE,
};

const DONE_TXT = {
  msg: 'Please try again later or reach out to Conviva Support.',
  btn: 'Contact',
  err: 'Something went wrong',
};

const getLoadingText = (maxQueryTime, showHeavyMetricMessage) => {
  const formattedTime = secondsToMinutes(maxQueryTime);
  return showHeavyMetricMessage
    ? `We are aggregating millions of events to fetch the data. Unique Devices based metric queries for duration greater than 30 days can take a long time to load.`
    : `We are aggregating millions of events and expect data to display within ${formattedTime}.`;
};

function WarningIcon() {
  return <WarningOutlined style={{ color: '#ff6347' }} className="mr-2" />;
}

export function DefaultLayer({
  progress,
  duration,
  showMsg = false,
  showHeavyMetricMessage = false,
}) {
  const maxQueryTime = calcProgressDuration(duration);

  return (
    <>
      <Layer data-testid="layer" key="layer" />
      <Loader data-testid="loader" key="loader">
        <Space key="progress-wrapper" direction="vertical">
          {progress ? <ProgressBar key="progress-bar" duration={duration} /> : <Spin />}
          {showMsg && (
            <Text key="loading-message">
              {getLoadingText(maxQueryTime, showHeavyMetricMessage)}
            </Text>
          )}
        </Space>
      </Loader>
    </>
  );
}

export function RetryCard({ onClick, horizontal = false, showHeavyMetricMessage = false }) {
  return (
    <CardWrapper horizontal={horizontal}>
      <span style={{ margin: '0px 10px' }}>
        <WarningIcon />
        {RETRY_TXT.err}
      </span>
      <div className="message">
        {showHeavyMetricMessage ? RETRY_TXT.heavyMetricMsg : RETRY_TXT.msg}
      </div>
      <Button onClick={onClick} type="primary" ghost>
        {RETRY_TXT.btn}
      </Button>
    </CardWrapper>
  );
}

export function ContactCard({ onClick, horizontal = false }) {
  return (
    <CardWrapper horizontal={horizontal}>
      <span style={{ margin: '0px 10px' }}>
        <WarningIcon />
        {DONE_TXT.err}
      </span>
      <div className="message">{DONE_TXT.msg}</div>
      <Button id="contact-button" onClick={onClick} type="primary" ghost>
        <a href="/app/pulse/support" target="_BLANK">
          {DONE_TXT.btn}
        </a>
      </Button>
    </CardWrapper>
  );
}

export function PreparingData({ horizontal = false }) {
  return (
    <>
      <Layer key="layer" />
      <Loader style={{ width: 'auto', maxWidth: 'none' }} key="loader">
        <CardWrapper horizontal={horizontal}>
          <div style={{ fontSize: '3em' }}>
            <PreparingIcon />
          </div>
          <div style={{ fontWeight: 400 }}>Preparing Data</div>
          <div style={{ fontSize: '0.9em', fontWeight: 300 }}>
            We are aggregating millions of events and expect data to display within 20 seconds.
          </div>
        </CardWrapper>
      </Loader>
    </>
  );
}

export function QueryError({ horizontal = false }) {
  return (
    <CardWrapper horizontal={horizontal}>
      <span style={{ margin: '0px 10px' }}>
        <WarningIcon />
        Query Error
      </span>
      <div className="message">
        Missing or invalid dimension while processing the query. Please update the filter and try
        again.
      </div>
    </CardWrapper>
  );
}
